import { assert } from '../../app/logger.js';
import { list_diff } from '../../util/diff.js';
import { Pipeable } from './pipeable.js';
import { PipeableObject } from './pipeable_object.js';

const metaProperties = [
  // Called when item is changed, argument: {is, idx}.
  'changed',

  // Called when the array has no items anymore.
  'cleared',

  // Called when item is deleted, argument: {idx}.
  'deleted',

  // Called when a new item is inserted, argument: {is, idx}.
  'inserted',

  // Called when a first item is inserted.
  'stocked',
];

//
// Basic pipeline mechanism to connect array data and data processors such as UI.
//
class PipeableArray extends PipeableObject {
  set(data) {
    assert(data instanceof Array, `[data] not array`);
    assert(
      this.constructor.Sort || data.every(item => typeof item == 'string'),
      `[data] no Sort function provided for non string PipeableArray`
    );

    let oldValues = this.depPropertyValues;
    this.#unpipeItems(this.data);
    this.removeDataDeps(this.data, this.depProperties);

    let oldData = this.data;

    // Retain a reference to the data, not a copy. If array is changed
    // then the original should be changed as well.
    this.data = data
      .map(item => this.#toItem(item))
      .sort(this.constructor.Sort);

    this.#pipeItems(this.data);
    this.addDataDeps(this.data, this.depProperties);

    this.notifyQualifying(oldData, oldValues);
    return this;
  }

  insert(itemData) {
    assert(this.data instanceof Array, `[data] not array`);
    assert(
      this.constructor.Sort || typeof itemData == 'string',
      `[data] no Sort function provided for non string PipeableArray`
    );

    let item = this.#toItem(itemData);
    this.#pipeItems([item]);
    this.addDataDeps([item], this.depProperties);
    let oldValues = this.depPropertyValues;

    let oldData = [...this.data];
    this.data.push(item);
    this.data.sort(this.constructor.Sort);

    this.notifyQualifying(oldData, oldValues);
    return item;
  }

  remove(item) {
    assert(this.data instanceof Array, `[data] not array`);

    let index = this.indexOf(item);
    assert(index != -1, `[data] no item found`);
    if (index != -1) {
      let oldValues = this.depPropertyValues;
      this.#unpipeItems([item]);
      this.removeDataDeps([item], this.depProperties);

      let oldData = [...this.data];
      this.data.splice(index, 1);

      this.notifyQualifying(oldData, oldValues);
    }
    return index;
  }

  pipe(receiverOrMap, { doNotNotify } = {}) {
    const map =
      receiverOrMap instanceof Function
        ? {
            array: receiverOrMap,
          }
        : receiverOrMap;

    let hasMetaReceivers = false;
    for (let property of Object.keys(map)) {
      let receiver = map[property];
      this.addReceiver(property, receiver);
      this.addDataDeps(this.data, [property]);
      if (!doNotNotify) {
        hasMetaReceivers |= metaProperties.indexOf(property) != -1;
        if (property in this) {
          let value = this[property];
          assert(value instanceof Array, `[data] ${property} is not array`);
          this.notifyReceiver(receiver, value, list_diff([], value));
        }
      }
    }
    if (hasMetaReceivers) {
      this.notifyMetaProperties(list_diff([], this.array), -1);
    }
    return this.createPipe(receiverOrMap);
  }

  unpipe(receiverOrMap) {
    const map =
      receiverOrMap instanceof Function
        ? {
            array: receiverOrMap,
          }
        : receiverOrMap;

    for (let property of Object.keys(map)) {
      let receiver = map[property];
      this.removeReceiver(property, receiver);
      if (!this.hasReceivers(property)) {
        this.removeDataDeps(this.data, [property]);
      }
    }
  }

  pipeItems(map) {
    this.#itemPipeMaps.push(map);
    this.#pipeItems(this.data, [map]);
  }

  unpipeItems(map) {
    let index = this.#itemPipeMaps.indexOf(map);
    assert(index != -1, `[data] no item pipe for ${map}`);
    if (index != -1) {
      this.#itemPipeMaps.splice(index, 1);
      this.#unpipeItems(this.data, [map]);
    }
  }

  get array() {
    return [...this.data].sort(this.constructor.Sort);
  }

  map(...args) {
    return (this.data && this.data.map(...args)) || [];
  }

  filter(...args) {
    return (this.data && this.data.filter(...args)) || [];
  }

  indexOf(itemData) {
    return this.data.findIndex(item => this.constructor.Is(item, itemData));
  }

  findBy(itemData) {
    let index = this.indexOf(itemData);
    return index != -1 && this.data[index];
  }

  [Symbol.iterator]() {
    return this.array[Symbol.iterator]();
  }

  // protected

  constructor() {
    super();

    // If sort function is given then selfpipe to keep the array rearranging.
    if (
      this.constructor.Sort &&
      Array.from(this.depProperties).includes('array')
    ) {
      this.addReceiver('array', this.#selfUpdateReceiver);
    }
  }

  notifyQualifying(oldData, oldDepValues) {
    this.notifyIfChanged('array', this.data, oldData);
    for (let { property, oldValue } of oldDepValues) {
      this.notifyIfChanged(property, this[property], oldValue);
    }
  }

  notifyIfChanged(property, value, oldValue) {
    assert(value instanceof Array, `${property} value is not array`);
    assert(oldValue instanceof Array, `${property} old value is not array`);

    let diff = list_diff(oldValue, value, this.constructor.Is);
    if (diff.length > 0) {
      this.notify(property, value, diff);

      // Notify meta receivers if any.
      if (property == 'array') {
        this.notifyMetaProperties(diff, oldValue.length);
      }
    }
  }

  notifyMetaProperties(diff, oldArrayLength) {
    for (let entry of diff) {
      this.notify(entry.what, entry);
    }
    if (this.data.length > 0 && (oldArrayLength == 0 || oldArrayLength == -1)) {
      this.notify('stocked');
    } else if (
      this.data.length == 0 &&
      (oldArrayLength > 0 || oldArrayLength == -1)
    ) {
      this.notify('cleared');
    }
  }

  // Register dependent property on casters.
  addDataDeps(casters, properties) {
    for (let property of properties) {
      let baseProperties = this.dataDeps[property];
      if (!baseProperties) {
        continue;
      }
      let hasReceivers = this.hasReceivers(property);
      if (!hasReceivers) {
        continue;
      }
      for (let caster of casters) {
        assert(
          caster instanceof Pipeable,
          `[data] not a datapipe, got ${caster}`
        );
        caster.addDataDep(this, property, baseProperties);
      }
    }
  }

  // Unregister dependent property on casters.
  removeDataDeps(casters, properties) {
    for (let property of properties) {
      if (!(property in this.dataDeps)) {
        continue;
      }
      for (let caster of casters) {
        if (caster instanceof Pipeable) {
          caster.removeDataDep(this, property);
        }
      }
    }
  }

  #toItem(itemData) {
    if (itemData?.constructor != Object) {
      return itemData;
    }
    return this.CreateItem(itemData);
  }

  #pipeItems(items, pipeMaps = this.#itemPipeMaps) {
    for (let item of items) {
      for (let pipeMap of pipeMaps) {
        item.pipe(pipeMap, {
          doNotNotify: true,
        });
      }
    }
  }

  #unpipeItems(items, pipeMaps = this.#itemPipeMaps) {
    for (let item of items) {
      for (let pipeMap of pipeMaps) {
        item.unpipe(pipeMap);
      }
    }
  }

  // Default value of an array item.
  static DefaultValue = [];

  // Sorting function to sort the array.
  static Sort;

  // Returns true if the given items are equal.
  static Is(a, b) {
    return a instanceof Date && b instanceof Date
      ? a.valueOf() == b.valueOf()
      : a == b;
  }

  // Item factory.
  CreateItem(a) {
    return a;
  }

  #itemPipeMaps = [];
  #selfUpdateReceiver = array => (this.data = array);
}

export { PipeableArray };
